import Vue from 'vue'
import {getWithPagination, LogsResource} from '../../util/api'

const initialState = {
  objects: {},
  runningLogs: {}, // All Logs that are currently running
  creatingObject: null, // The object currently being created
  editingObject: null, // The Object currently being editing
  createdObject: null, // The most recent Object to be created
  updatedObject: null, // The most recent Object to be updatd
  getInProgress: false, // Is a GET request in progress for this Store?
  createInProgress: false, // Is a GET request in progress for this Store?
  updateInProgress: false, // Is a POST or PUT request in progress for this Store?
  deleteInProgress: false, // Is a DELETE request in progress for this Store?
  inEditLogComment: '',
}

export default {
  state: { ...initialState },
  mutations: {
    SET_LOGS (state, objects) {
      let updatedObjects = {}
      let updatedRunningLogs = {}
      for (let key in objects) {
        if (objects.hasOwnProperty(key)) {
          if (objects[key].deleted_at === null) {
            // Stage the update so we can just do one Vue.set
            updatedObjects[objects[key].id] = objects[key]
          } else if (state.objects.hasOwnProperty(objects[key].id)) {
            // Immediately delete the object
            Vue.delete(state.objects, objects[key].id)
          }
          if (objects[key].end === null) { // Add the log to list of running logs if it is running
            updatedRunningLogs[objects[key].id] = objects[key]
          } else if (state.runningLogs.hasOwnProperty(objects[key].id) && objects[key].end !== null) { // Remove the log from list of running if it has stopped
            Vue.delete(state.runningLogs, objects[key].id) // Vue will take care of wether the key is even in the runningLogs list
          }
        }
      }
      state.objects = {
        ...state.objects,
        ...updatedObjects
      }

      state.runningLogs = {
        ...state.runningLogs,
        ...updatedRunningLogs
      }
    },
    SET_LOG (state, object) {
      if (object.deleted_at === null) {
        Vue.set(state.objects, object.id, object)
      } else {
        Vue.delete(state.objects, object.id)
      }

      if (object.end === null) { // Add the log to list of running logs if it is running
        Vue.set(state.runningLogs, object.id, object)
      } else if (object.end !== null) { // Remove the log from list of running if it has stopped
        Vue.delete(state.runningLogs, object.id) // Vue will take care of wether the key is even in the runningLogs list
      }
    },
    UNSET_LOG (state, object) {
      Vue.delete(state.runningLogs, object.id) // Vue will take care of wether the key is even in the runningLogs list
      Vue.delete(state.objects, object.id) // Vue will take care of wether the key is even in the runningLogs list
    },
    SET_RUNNING_LOG (state, object) {
      state.runningLog = object
    },
    CREATING_LOG (state, object) {
      state.creatingObject = object
    },
    EDITING_LOG (state, object) {
      state.editingObject = object
    },
    CREATED_LOG (state, object) {
      state.createdObject = object
    },
    UPDATED_LOG (state, object) {
      state.updatedObject = object
    },
    DELETED_LOG (state, object) {
      state.updatedObject = object
    },

    // Progress States
    GET_LOG_IN_PROGRESS (state, value) {
      state.getInProgress = value
    },
    CREATE_LOG_IN_PROGRESS (state, value) {
      state.createInProgress = value
    },
    UPDATE_LOG_IN_PROGRESS (state, value) {
      state.updateInProgress = value
    },
    DELETE_LOG_IN_PROGRESS (state, value) {
      state.deleteInProgress = value
    },

    SET_IN_EDIT_LOG_COMMENT (state, value) {
      state.inEditLogComment = value
    },

    LOGS_CLEAR_ALL (state) {
      for (let key in initialState) {
        Vue.set(state, key, initialState[key])
      }
    }
  },

  getters: {
    runningLog (state, getters, rootState) {
      let log = null // Default return
      // Search for running log with the same user id of the current user
      for (let i in state.runningLogs) {
        if (rootState.users.currentUser && state.runningLogs[i].user_id === rootState.users.currentUser.id) {
          log = state.runningLogs[i]
          break
        }
      }

      return log
    }
  },

  actions: {
    // Retrieve a single log
    getLog ({ commit }, id) {
      try {
        // Check if we have an up-to-date version of the object. If not, get it.
        commit('GET_LOG_IN_PROGRESS', true)
        LogsResource.get({id: id}).then((response) => {
          commit('GET_LOG_IN_PROGRESS', false)
          commit('SET_LOG', response.body)
        }, () => {
          commit('GET_LOG_IN_PROGRESS', false)
          // @TODO: Implement error handling
        })
      } catch (ex) {
        // Request was most likely cancelled due to existing in flight request
        commit('GET_LOG_IN_PROGRESS', false)
      }
    },

    // Retrieve many logs, based on supplied GET params
    getLogs ({ commit }, params) {
      commit('GET_LOG_IN_PROGRESS', true)
      getWithPagination(LogsResource, params, response => {
        // Note: Pagination requests return the objects under the 'data' key
        commit('SET_LOGS', response.body.data)
      }, () => {
        // Completion handler
        commit('GET_LOG_IN_PROGRESS', false)
      }, () => {
        // Error handler
        commit('GET_LOG_IN_PROGRESS', false)
      })
    },
    createLog ({ commit }, object) {
      // Create (POST) a LOG
      commit('CREATED_LOG', null)
      commit('CREATE_LOG_IN_PROGRESS', object)
      LogsResource.save({}, object).then((response) => {
        commit('CREATE_LOG_IN_PROGRESS', false)
        commit('CREATED_LOG', response.body)
        commit('SET_LOG', response.body)
      }, () => {
        console.error('Create LOG Failed')
        commit('CREATE_LOG_IN_PROGRESS', false)
        commit('CREATED_LOG', null)
      })
    },

    updateLog ({ commit }, object) {
      // Update (PUT) a LOG
      commit('UPDATED_LOG', null)
      commit('UPDATE_LOG_IN_PROGRESS', object)
      LogsResource.update({id: object.id}, object).then((response) => {
        commit('UPDATE_LOG_IN_PROGRESS', false)
        commit('UPDATED_LOG', response.body)
        commit('SET_LOG', response.body)
      }, () => {
        commit('UPDATE_LOG_IN_PROGRESS', false)
        commit('UPDATED_LOG', null)
      })
    },

    deleteLog ({ commit }, object) {
      // Update (PUT) a LOG
      commit('DELETED_LOG', null)
      commit('DELETE_LOG_IN_PROGRESS', object)
      LogsResource.delete({id: object.id}).then(() => {
        commit('DELETE_LOG_IN_PROGRESS', false)
        commit('DELETED_LOG', object)
        commit('UNSET_LOG', object)
      }, () => {
        commit('DELETE_LOG_IN_PROGRESS', false)
        commit('DELETED_LOG', null)
      })
    },

    setInEditLogComment ({commit}, value) {
      commit('SET_IN_EDIT_LOG_COMMENT', value)
    },

    clearAll ({ commit }) {
      commit('LOGS_CLEAR_ALL')
    }
  }
}
